import React from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import ExpandableDetails from "../../layout/ExpandableDetails";
import { colors, fonts } from "../../../styles/theme";

export interface ChoiceTile {
  id: string;
  title: string;
  /** Kleine graue Zeile darunter, höchstens drei Zeilen – z. B. voller Wortlaut oder Beispiele */
  caption?: string;
}

interface ChoiceTilesProps {
  tiles: ChoiceTile[];
  /** Weitere Kacheln, die erst nach dem Aufklappen erscheinen */
  moreTiles?: ChoiceTile[];
  onSelect: (id: string) => void;
}

const tileGrid = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
  gap: 2,
} as const;

/**
 * Erster Schritt eines Anlegen-Dialogs: die grundlegende Wahl als Kacheln, drei je Zeile. Jede
 * Kachel zeigt ihren Titel und darunter klein eine Erläuterung; ein Klick wählt sie und führt
 * zum Formular. Keine Kachel ist hervorgehoben. Das Muster steht in components/CLAUDE.md
 * („Auswahl vor dem Formular“).
 */
const ChoiceTiles: React.FC<ChoiceTilesProps> = ({
  tiles,
  moreTiles = [],
  onSelect,
}) => {
  const tile = (choice: ChoiceTile) => (
    <ButtonBase
      key={choice.id}
      onClick={() => onSelect(choice.id)}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        gap: 0.75,
        p: 2,
        height: "100%",
        textAlign: "left",
        border: `1px solid ${colors.border.divider}`,
        borderRadius: 2,
        transition: "border-color 0.15s, background-color 0.15s",
        "&:hover, &.Mui-focusVisible": {
          borderColor: colors.brand.primary,
          backgroundColor: colors.brand.primaryLighter,
        },
      }}
    >
      <Typography
        sx={{
          fontWeight: fonts.weights.semiBold,
          color: colors.text.primary,
        }}
      >
        {choice.title}
      </Typography>
      {choice.caption && (
        <Typography
          variant="caption"
          sx={{
            color: colors.text.secondary,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {choice.caption}
        </Typography>
      )}
    </ButtonBase>
  );

  const grid = <Box sx={tileGrid}>{tiles.map(tile)}</Box>;
  if (moreTiles.length === 0) return grid;

  return (
    <ExpandableDetails
      details={<Box sx={{ ...tileGrid, mt: 2 }}>{moreTiles.map(tile)}</Box>}
    >
      {grid}
    </ExpandableDetails>
  );
};

export default ChoiceTiles;
